<template>
  <span
    class="pay-range-version-bar"
    :class="[
      `pay-range-version-bar--${normalizedState}`,
      { 'pay-range-version-bar--selected': selected },
    ]"
    role="img"
    :aria-label="accessibleLabel"
    :title="label"
    @click="$emit('click', $event)"
  >
    <span class="pay-range-version-bar__label">{{ label }}</span>
  </span>
</template>

<script setup>
import { computed } from "vue";
import {
  formatVersionBarAriaLabel,
  formatVersionBarLabel,
  normalizeVersionBarState,
} from "./payRangeVersionBarUtils";

const props = defineProps({
  /** Type variant: lapsed | current | future | draft */
  state: {
    type: String,
    default: "lapsed",
  },
  versionNumber: {
    type: [Number, String],
    required: true,
  },
  /** Range bottom (the timeline's `bottom` field). */
  amountMin: {
    type: [Number, String],
    default: null,
  },
  /** Range top (the timeline's `top` field). */
  amountMax: {
    type: [Number, String],
    default: null,
  },
  /** Selected bar state (ER-11556) — ring only, fill unchanged. */
  selected: {
    type: Boolean,
    default: false,
  },
  /** Range currency prefix, default "$". */
  currencySymbol: {
    type: String,
    default: "$",
  },
});

const normalizedState = computed(() => normalizeVersionBarState(props.state));

const label = computed(() =>
  formatVersionBarLabel(props.versionNumber, props.amountMin, props.amountMax, props.currencySymbol)
);

const accessibleLabel = computed(() =>
  formatVersionBarAriaLabel(
    normalizedState.value,
    props.versionNumber,
    props.amountMin,
    props.amountMax,
    props.currencySymbol,
    props.selected
  )
);
</script>

<style scoped>
.pay-range-version-bar {
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  height: 26px;
  padding: 0 11px;
  border-radius: 6px;
  border: 1.5px solid transparent;
  max-width: 100%;
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 11px;
  font-weight: 400;
  font-style: normal;
  line-height: normal;
  overflow: hidden;
  white-space: nowrap;
  transition: box-shadow 0.12s ease;
  cursor: pointer;
}

.pay-range-version-bar__label {
  display: block;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* Type colors (ER-11556): Lapsed grey + black stroke, Current light green no
   stroke, Future light blue no stroke, Draft fuchsia tint + dashed stroke. */
.pay-range-version-bar--lapsed {
  --pvb-ring: #101828;
  background: #e3e7ec;
  border-color: #101828;
  color: #667085;
}

.pay-range-version-bar--current {
  --pvb-ring: #027a48;
  background: #e8fbf0;
  border-color: transparent;
  color: #027a48;
}

.pay-range-version-bar--future {
  --pvb-ring: #175cd3;
  background: #f0f9ff;
  border-color: transparent;
  color: #101828;
}

.pay-range-version-bar--draft {
  --pvb-ring: var(--component-colors-utility-fuchsia-utility-fuchsia-500, #d444f1);
  background: var(--component-colors-utility-fuchsia-utility-fuchsia-50, #fdf4ff);
  border-color: var(--component-colors-utility-fuchsia-utility-fuchsia-500, #d444f1);
  border-style: dashed;
  color: var(--colors-text-text-primary-900, #101828);
}

/* Hover — subtle elevation shadow only; fill color never changes. */
.pay-range-version-bar:hover {
  box-shadow: 0 2px 6px rgba(16, 24, 40, 0.18);
}

/* Selected — same brand border as the history row (1.5px #7F56D9). */
.pay-range-version-bar--selected,
.pay-range-version-bar--selected:hover {
  border: 1.5px solid #7f56d9;
  border-style: solid;
  box-shadow: none;
}
</style>
